import { Card, Form, message, Typography } from 'antd';
import { Box } from 'components/Box/Box';
import { BaseLayout } from 'features/navigation/components';
import React, { useEffect, useMemo, useState } from 'react';
import {
  createStatusNewsItem,
  deleteStatusNewsItem,
  fetchStatusNewsItems,
  updateStatusNewsItem,
} from '../api/statusNewsAPI';
import StatusNewsEditModal from '../components/StatusNewsEditModal';
import StatusNewsForm from '../components/StatusNewsForm';
import StatusNewsList from '../components/StatusNewsList';
type StatusBarType = 'info' | 'success' | 'error';

export type StatusNewsItem = {
  id: string;
  type: StatusBarType;
  title?: string;
  text: string;
  leftAlign?: boolean;
  enabled: boolean;
  priority?: number;
  linkText?: string;
  linkUrl?: string;
  createdAt?: number;
};

type CreateValues = Omit<StatusNewsItem, 'id' | 'createdAt'>;
type EditValues = Partial<Omit<StatusNewsItem, 'id' | 'createdAt'>>;

const AdminPage: React.FC = () => {
  const [items, setItems] = useState<StatusNewsItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Fix: Always call hooks in the same order, never conditionally

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const [editingItem, setEditingItem] = useState<StatusNewsItem | null>(null);

  const TYPE_ORDER: Record<StatusBarType, number> = {
    info: 0,
    success: 1,
    error: 2,
  };

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const ta = TYPE_ORDER[a.type] ?? 99;
      const tb = TYPE_ORDER[b.type] ?? 99;
      if (ta !== tb) {
        return ta - tb;
      }
      const pa = a.priority ?? 999;
      const pb = b.priority ?? 999;
      if (pa !== pb) {
        return pa - pb;
      }
      const ca = a.createdAt ?? 0;
      const cb = b.createdAt ?? 0;
      return cb - ca; // newer first
    });
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await fetchStatusNewsItems();
      setItems(data);
    } catch {
      message.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleCreate = async (values: CreateValues) => {
    setLoading(true);
    try {
      await createStatusNewsItem({
        ...values,
        // normalize
        priority: values.priority ?? 1,
        leftAlign: !!values.leftAlign,
        enabled: values.enabled ?? true,
        createdAt: Date.now(),
      });
      message.success('Status news item created');
      createForm.resetFields();
      await loadItems();
    } catch {
      message.error('Failed to create item');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (item: StatusNewsItem) => {
    setEditingItem(item);
    editForm.setFieldsValue({
      type: item?.type ?? 'info',
      title: item?.title ?? '',
      text: item?.text ?? '',
      enabled: item?.enabled ?? true,
      leftAlign: !!item.leftAlign,
      priority: item?.priority ?? 1,
      linkText: item?.linkText ?? null,
      linkUrl: item?.linkUrl ?? null,
    });
  };

  const handleEditSave = async (values: EditValues) => {
    if (!editingItem) {
      return;
    }
    setLoading(true);
    try {
      await updateStatusNewsItem(editingItem.id, {
        ...editingItem,
        ...values,
        // normalize booleans & priority
        enabled: values.enabled ?? editingItem.enabled,
        leftAlign: values.leftAlign ?? editingItem.leftAlign ?? false,
        priority: values.priority ?? editingItem.priority ?? 1,
      });
      message.success('Item updated');
      setEditingItem(null);
      editForm.resetFields();
      await loadItems();
    } catch {
      message.error('Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (item: StatusNewsItem) => {
    setLoading(true);
    try {
      await updateStatusNewsItem(item.id, { enabled: !item.enabled });
      message.success(`Item ${!item.enabled ? 'enabled' : 'disabled'}`);
      await loadItems();
    } catch {
      message.error('Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await deleteStatusNewsItem(id);
      message.success('Item deleted');
      await loadItems();
    } catch {
      message.error('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseLayout>
      <Typography.Title level={2} style={{ marginTop: 0 }}>
        Admin
      </Typography.Title>
      {/* Form to create */}
      <Box maxWidth="400px" marginBottom="24px">
        <Card>
          <StatusNewsForm
            form={createForm}
            loading={loading}
            onSubmit={handleCreate}
          />
        </Card>
      </Box>
      {/* Items */}
      <StatusNewsList
        items={sortedItems}
        loading={loading}
        onToggleEnabled={(_id, _enabled) => {
          const item = items.find((i) => i.id === _id);
          if (item) {
            handleToggleEnabled(item);
          }
        }}
        onEdit={openEdit}
        onDelete={handleDelete}
      />
      {/* Edit modal */}
      <StatusNewsEditModal
        visible={!!editingItem}
        loading={loading}
        editingItem={editingItem}
        editForm={editForm}
        onSubmit={handleEditSave}
        onCancel={() => {
          setEditingItem(null);
          editForm.resetFields();
        }}
      />
    </BaseLayout>
  );
};

export { AdminPage };
